import type { IChartData } from "../../scheme/components/analytics/interfaces";
import type { IMultiAnalysisDataMark } from "../types";
import { buildMultiAnalysisMeasureKey } from "./trend-measure-list";

export interface ITimePanelMeasureListItem {
  key: string;
  idPoint: number;
  idMeasure: number | null;
  color: string;
  pointName: string;
  measureName: string;
  parameterName: string;
  units: string;
  unitCode: number | null;
}

export const getTimePanelMeasureKey = (
  idPoint: number | null | undefined,
  measureName: string | null | undefined,
  fallbackName?: string | null,
  idMeasure?: number | null,
): string | null => buildMultiAnalysisMeasureKey(idPoint, measureName, fallbackName, idMeasure);

const SERIES_LABEL_SEPARATOR = " / ";

const splitSeriesLabel = (
  series: Pick<IChartData, "name" | "parameterName">,
): { pointName: string; measureName: string } => {
  const separatorIndex = series.name.indexOf(SERIES_LABEL_SEPARATOR);

  if (separatorIndex === -1) {
    return {
      pointName: series.name,
      measureName: series.parameterName ?? series.name,
    };
  }

  return {
    pointName: series.name.slice(0, separatorIndex),
    measureName:
      series.name.slice(separatorIndex + SERIES_LABEL_SEPARATOR.length) || series.parameterName || series.name,
  };
};

const findExistingTimePanelMeasure = (
  items: Map<string, ITimePanelMeasureListItem>,
  idPoint: number,
  measureName: string,
  idMeasure?: number | null,
): ITimePanelMeasureListItem | undefined => {
  for (const item of items.values()) {
    if (item.idPoint !== idPoint) {
      continue;
    }

    if (idMeasure != null && item.idMeasure != null) {
      if (item.idMeasure === idMeasure) {
        return item;
      }

      continue;
    }

    if (measureName && (item.measureName === measureName || item.parameterName === measureName)) {
      return item;
    }
  }

  return undefined;
};

export const buildTimePanelMeasureListItems = (
  dataMarks: readonly IMultiAnalysisDataMark[],
  trendSeries: readonly IChartData[],
): ITimePanelMeasureListItem[] => {
  const items = new Map<string, ITimePanelMeasureListItem>();

  trendSeries.forEach(series => {
    const key = getTimePanelMeasureKey(series.idPoint, series.parameterName, series.name, series.idMeasure);

    if (!key || series.idPoint == null || items.has(key)) {
      return;
    }

    const label = splitSeriesLabel(series);

    if (findExistingTimePanelMeasure(items, series.idPoint, label.measureName, series.idMeasure)) {
      return;
    }

    items.set(key, {
      key,
      idPoint: series.idPoint,
      idMeasure: Number.isFinite(Number(series.idMeasure)) ? Number(series.idMeasure) : null,
      color: series.color,
      pointName: label.pointName,
      measureName: label.measureName,
      parameterName: series.parameterName ?? "",
      units: series.units,
      unitCode: Number.isFinite(Number(series.unitCode)) ? Number(series.unitCode) : null,
    });
  });

  dataMarks.forEach(mark => {
    const key = getTimePanelMeasureKey(mark.idPoint, mark.measureName, undefined, mark.idMeasure);
    const existing = findExistingTimePanelMeasure(items, mark.idPoint, mark.measureName, mark.idMeasure);

    if (existing) {
      if (mark.measureName) {
        existing.measureName = mark.measureName;
      }

      return;
    }

    if (!key || items.has(key)) {
      return;
    }

    items.set(key, {
      key,
      idPoint: mark.idPoint,
      idMeasure: Number.isFinite(Number(mark.idMeasure)) ? Number(mark.idMeasure) : null,
      color: "#8b93a7",
      pointName: mark.pointName,
      measureName: mark.measureName,
      parameterName: "",
      units: "",
      unitCode: Number.isFinite(Number(mark.units)) ? Number(mark.units) : null,
    });
  });

  return Array.from(items.values());
};

export const isTimePanelMeasureSelected = (
  measureKey: string | null,
  selectedMeasureKeys: ReadonlySet<string>,
): boolean => selectedMeasureKeys.size === 0 || (measureKey != null && selectedMeasureKeys.has(measureKey));

export const isTimePanelMeasureHidden = (measureKey: string | null, hiddenMeasureKeys: ReadonlySet<string>): boolean =>
  measureKey != null && hiddenMeasureKeys.has(measureKey);

export const toggleTimePanelMeasureKey = (
  selectedMeasureKeys: ReadonlySet<string>,
  measureKey: string,
): Set<string> => {
  const next = new Set(selectedMeasureKeys);

  if (next.has(measureKey)) {
    next.delete(measureKey);
  } else {
    next.add(measureKey);
  }

  return next;
};

export const pruneTimePanelMeasureKeys = (
  selectedMeasureKeys: ReadonlySet<string>,
  availableKeys: ReadonlySet<string>,
): Set<string> => new Set([...selectedMeasureKeys].filter(key => availableKeys.has(key)));
